import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getDecryptedUserFromCookie, getSessionMainId } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const TOAST_OPTS = {
    position: "top-center",
    autoClose: 2500,
};

const MOBILE_REGEX = /^[6-9]\d{9}$/;
const PINCODE_REGEX = /^[1-9]\d{5}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const showWarning = (message) => toast.warning(message, TOAST_OPTS);
const showError = (message) => toast.error(message, TOAST_OPTS);
const showSuccess = (message) => toast.success(message, TOAST_OPTS);

const ORDER_STATUSES = [
    "Pending",
    "Processing",
    "Packed",
    "Shipped",
    "Out for delivery",
    "Delivered",
    "Cancelled",
];

const PAYMENT_MODES = [
    "Cash on Delivery",
    "Admin Manual",
    "Pay Online",
];

const parseAddress = (value) => {
    if (!value) return {};
    if (typeof value === "object") return value;
    try {
        return JSON.parse(value);
    } catch {
        return {};
    }
};

const getCustomerRowKey = (c) => {
    if (!c) return "";
    const cid = c.cust_ID != null ? String(c.cust_ID).trim() : "";
    if (cid) return cid;
    return c.id != null ? String(c.id) : "";
};

const customerBaseName = (c) =>
    c?.displayName || c?.companyName || c?.customerName || getCustomerRowKey(c) || "";

const customerSelectLabel = (c) => {
    const name = customerBaseName(c);
    const key = getCustomerRowKey(c);
    if (name && key && name !== key) return `${name} - ${key}`;
    return name || key || "";
};

const toCartLine = (product, qty = 1) => {
    const type = product.product_type || "Simple Product";
    let price = parseFloat(product.originalPrice || product.price || 0);
    let salePrice = parseFloat(
        product.discountPrice || product.salePrice || product.originalPrice || product.price || 0
    );
    let variation_attributes = null;
    let sku = product.sku || product.product_sku || "";

    if (type === "Variable Product" && Array.isArray(product.variation) && product.variation.length > 0) {
        const v = product.variation[0];
        price = parseFloat(v.price || price);
        salePrice = parseFloat(v.salePrice || v.price || salePrice);
        variation_attributes = v.attributes || null;
        sku = v.sku || sku;
    }

    return {
        product_id: product.product_id,
        product_sku: sku,
        product_name: product.title,
        product_price: price,
        product_saleprice: salePrice,
        product_image: product.image || "",
        product_type: type,
        variation_attributes,
        quantity: Math.max(1, Number(qty) || 1),
    };
};

const AdminOrderFormModal = ({ showModal, closeModal, onOrderCreated }) => {
    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const mainId = getSessionMainId() || isLoggedIn?.main_id || isLoggedIn?.user_id;

    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [productSearch, setProductSearch] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [guestMode, setGuestMode] = useState(false);
    const [guestName, setGuestName] = useState("");
    const [guestMobile, setGuestMobile] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    const [lineItems, setLineItems] = useState([]);
    const [deliveryCharge, setDeliveryCharge] = useState("0");
    const [discountPrice, setDiscountPrice] = useState("0");
    const [paymentMode, setPaymentMode] = useState("Cash on Delivery");
    const [orderStatus, setOrderStatus] = useState("Pending");
    const [customerNote, setCustomerNote] = useState("");
    const [sameAsBilling, setSameAsBilling] = useState(true);
    const [billing, setBilling] = useState({
        attention: "",
        city: "",
        new_address: "",
        pincode: "",
        number: "",
        state: "",
    });
    const [shipping, setShipping] = useState({
        attention: "",
        city: "",
        address: "",
        pincode: "",
        number: "",
        state: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const resetForm = () => {
        setSelectedCustomer(null);
        setGuestMode(false);
        setGuestName("");
        setGuestMobile("");
        setGuestEmail("");
        setLineItems([]);
        setProductSearch("");
        setDeliveryCharge("0");
        setDiscountPrice("0");
        setPaymentMode("Cash on Delivery");
        setOrderStatus("Pending");
        setCustomerNote("");
        setSameAsBilling(true);
        setBilling({ attention: "", city: "", new_address: "", pincode: "", number: "", state: "" });
        setShipping({ attention: "", city: "", address: "", pincode: "", number: "", state: "" });
    };

    useEffect(() => {
        if (!showModal || !mainId) return;

        const fetchCustomers = async () => {
            try {
                const response = await fetch(`${url}/api/customer/all/${mainId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await response.json();
                setCustomers(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                showError("Failed to load customers.");
            }
        };

        const fetchProducts = async () => {
            try {
                const response = await fetch(`${url}/grouped-product/search-all-products/${mainId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await response.json();
                setProducts(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                showError("Failed to load products.");
            }
        };

        fetchCustomers();
        fetchProducts();
    }, [showModal, mainId, token, url]);

    const customerOptions = useMemo(
        () =>
            customers
                .filter((c) => getCustomerRowKey(c))
                .map((c) => ({
                    value: getCustomerRowKey(c),
                    label: customerSelectLabel(c),
                    customer: c,
                })),
        [customers]
    );

    const filteredProducts = useMemo(() => {
        const q = productSearch.trim().toLowerCase();
        if (!q) return [];
        return products
            .filter((p) => (p.title || "").toLowerCase().includes(q))
            .slice(0, 12);
    }, [products, productSearch]);

    const subtotal = useMemo(
        () =>
            lineItems.reduce(
                (sum, item) => sum + Number(item.product_saleprice || 0) * Number(item.quantity || 1),
                0
            ),
        [lineItems]
    );

    const amountBeforeDiscount = subtotal + Number(deliveryCharge || 0);
    const amountAfterDiscount = Math.max(0, amountBeforeDiscount - Number(discountPrice || 0));

    const validateForm = () => {
        const messages = [];

        const pushMessage = (message) => {
            if (!messages.includes(message)) messages.push(message);
        };

        if (guestMode) {
            if (!guestName.trim()) {
                pushMessage("Guest name is required.");
            } else if (guestName.trim().length < 2) {
                pushMessage("Guest name must be at least 2 characters.");
            }

            const mobileDigits = guestMobile.replace(/\D/g, "");
            if (!mobileDigits) {
                pushMessage("Guest mobile number is required.");
            } else if (!MOBILE_REGEX.test(mobileDigits)) {
                pushMessage("Enter a valid 10-digit mobile number.");
            }

            if (guestEmail.trim() && !EMAIL_REGEX.test(guestEmail.trim())) {
                pushMessage("Enter a valid email address.");
            }
        } else if (!selectedCustomer?.customer) {
            pushMessage("Select a customer or enable walk-in / guest customer.");
        }

        if (lineItems.length === 0) {
            pushMessage("Add at least one product to the order.");
        } else {
            const invalidQty = lineItems.some(
                (item) => !Number.isFinite(Number(item.quantity)) || Number(item.quantity) < 1
            );
            const invalidPrice = lineItems.some(
                (item) => !Number.isFinite(Number(item.product_saleprice)) || Number(item.product_saleprice) <= 0
            );
            if (invalidQty) {
                pushMessage("Each product must have a quantity of at least 1.");
            }
            if (invalidPrice) {
                pushMessage("One or more products have an invalid price.");
            }
        }

        const deliveryNum = Number(deliveryCharge);
        if (deliveryCharge !== "" && (!Number.isFinite(deliveryNum) || deliveryNum < 0)) {
            pushMessage("Delivery charge cannot be negative.");
        }

        const discountNum = Number(discountPrice);
        if (discountPrice !== "" && (!Number.isFinite(discountNum) || discountNum < 0)) {
            pushMessage("Discount cannot be negative.");
        } else if (Number.isFinite(discountNum) && discountNum > amountBeforeDiscount) {
            pushMessage("Discount cannot be greater than order subtotal.");
        }

        if (subtotal <= 0) {
            pushMessage("Order total must be greater than zero.");
        }

        if (amountAfterDiscount <= 0) {
            pushMessage("Final order amount must be greater than zero.");
        }

        const billingMobile = (guestMode ? guestMobile : billing.number).replace(/\D/g, "");
        if (!billingMobile) {
            pushMessage("Billing mobile number is required.");
        } else if (!MOBILE_REGEX.test(billingMobile)) {
            pushMessage("Enter a valid 10-digit billing mobile number.");
        }

        if (!billing.city.trim()) {
            pushMessage("Billing city is required.");
        }
        if (!billing.new_address.trim()) {
            pushMessage("Billing address is required.");
        }
        if (!billing.pincode.trim()) {
            pushMessage("Billing pincode is required.");
        } else if (!PINCODE_REGEX.test(billing.pincode.trim())) {
            pushMessage("Enter a valid 6-digit billing pincode.");
        }

        if (!sameAsBilling) {
            if (!shipping.city.trim()) {
                pushMessage("Shipping city is required.");
            }
            if (!shipping.address.trim()) {
                pushMessage("Shipping address is required.");
            }
            if (!shipping.pincode.trim()) {
                pushMessage("Shipping pincode is required.");
            } else if (!PINCODE_REGEX.test(shipping.pincode.trim())) {
                pushMessage("Enter a valid 6-digit shipping pincode.");
            }
        }

        if (!paymentMode) {
            pushMessage("Payment mode is required.");
        }
        if (!orderStatus) {
            pushMessage("Order status is required.");
        }

        return { valid: messages.length === 0, messages };
    };

    const runValidation = () => {
        const result = validateForm();
        if (!result.valid) {
            showWarning(result.messages[0]);
        }
        return result.valid;
    };

    const applyCustomerToForm = (customer) => {
        if (!customer) return;
        const billingAddr = parseAddress(customer.billingAddress);
        const shippingAddr = parseAddress(customer.shippingAddress);
        const contact = Array.isArray(customer.contact)
            ? customer.contact[0]
            : parseAddress(customer.contact);

        setBilling({
            attention: customerBaseName(customer),
            city: billingAddr.city || "",
            new_address: billingAddr.address || billingAddr.new_address || "",
            pincode: billingAddr.pincode || "",
            number: billingAddr.number || contact?.mobile || contact?.phone || "",
            state: billingAddr.state || "",
        });
        setShipping({
            attention: customerBaseName(customer),
            city: shippingAddr.city || billingAddr.city || "",
            address: shippingAddr.address || billingAddr.address || billingAddr.new_address || "",
            pincode: shippingAddr.pincode || billingAddr.pincode || "",
            number: shippingAddr.number || billingAddr.number || contact?.mobile || contact?.phone || "",
            state: shippingAddr.state || billingAddr.state || "",
        });
        setGuestMobile(contact?.mobile || contact?.phone || billingAddr.number || "");
        setGuestEmail(contact?.email || customer.email || "");
        setGuestName(customerBaseName(customer));
    };

    const handleCustomerChange = (option) => {
        setSelectedCustomer(option);
        setGuestMode(false);
        if (option?.customer) {
            applyCustomerToForm(option.customer);
        }
    };

    const addProduct = (product) => {
        const line = toCartLine(product, 1);
        if (!Number.isFinite(Number(line.product_saleprice)) || Number(line.product_saleprice) <= 0) {
            showWarning("This product has no valid price and cannot be added.");
            return;
        }
        setLineItems((prev) => {
            const idx = prev.findIndex(
                (p) => p.product_id === line.product_id && p.product_sku === line.product_sku
            );
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: Number(next[idx].quantity || 1) + 1 };
                return next;
            }
            return [...prev, line];
        });
        setProductSearch("");
        showSuccess(`${line.product_name} added to order.`);
    };

    const updateLineQty = (index, qty) => {
        const parsed = Number(qty);
        if (qty !== "" && (!Number.isFinite(parsed) || parsed < 1)) {
            showWarning("Quantity must be at least 1.");
            return;
        }
        setLineItems((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, quantity: Math.max(1, Number(qty) || 1) } : item
            )
        );
    };

    const removeLine = (index) => {
        setLineItems((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!runValidation()) return;

        const user_name = guestMode ? guestName.trim() : customerBaseName(selectedCustomer?.customer);
        const user_id = guestMode
            ? `WALKIN-${Date.now()}`
            : getCustomerRowKey(selectedCustomer?.customer);

        const billing_address = {
            ...billing,
            number: (guestMode ? guestMobile : billing.number || "").replace(/\D/g, ""),
        };
        const shipping_address = sameAsBilling
            ? {
                attention: billing_address.attention,
                city: billing_address.city,
                address: billing_address.new_address,
                pincode: billing_address.pincode,
                number: billing_address.number,
                state: billing_address.state,
            }
            : shipping;

        const isCod = /cash on delivery|cod|pay on delivery/i.test(paymentMode);

        setSubmitting(true);
        try {
            const response = await fetch(`${url}/payment/admin-create-order`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    user_id,
                    user_name,
                    name: user_name,
                    mobile: guestMode ? guestMobile.replace(/\D/g, "") : billing_address.number,
                    email: guestEmail.trim(),
                    cart_products: lineItems,
                    billing_address,
                    shipping_address,
                    amountbeforediscount: amountBeforeDiscount.toFixed(2),
                    amountafterdiscount: amountAfterDiscount.toFixed(2),
                    discountprice: Number(discountPrice || 0).toFixed(2),
                    deliveryCharge: String(deliveryCharge || 0),
                    payment_mode: paymentMode,
                    orderStatus,
                    customer_note: customerNote.trim(),
                    remaining_amount: isCod ? amountAfterDiscount.toFixed(2) : "0",
                    totalAmountbeforePay: amountAfterDiscount.toFixed(2),
                }),
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || "Failed to create order.");
            }

            showSuccess(result.message || "Order created successfully.");
            resetForm();
            closeModal();
            if (typeof onOrderCreated === "function") onOrderCreated();
        } catch (err) {
            console.error(err);
            showError(err.message || "Failed to create order.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!showModal) return null;

    return (
        <div className="ls-enquiry-modal" onClick={closeModal}>
            <div
                className="ls-enquiry-modal-content"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="quotation-header">
                    <h2><i className="fa-solid fa-cart-plus"></i> Create Order</h2>
                    <button type="button" className="quotation-close" onClick={closeModal}>
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    <div className="ls-form-group">
                        <label>Customer *</label>
                        <Select
                            isClearable
                            isDisabled={guestMode}
                            options={customerOptions}
                            value={selectedCustomer}
                            onChange={handleCustomerChange}
                            placeholder="Search existing customer..."
                        />
                    </div>

                    <div className="ls-form-group">
                        <label className="lsuv-bulk-delete-select" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={guestMode}
                                onChange={(e) => {
                                    setGuestMode(e.target.checked);
                                    if (e.target.checked) setSelectedCustomer(null);
                                }}
                            />
                            <span>Walk-in / guest customer</span>
                        </label>
                    </div>

                    {guestMode && (
                        <div className="ls-form-row">
                            <div className="ls-form-group">
                                <label>Guest Name *</label>
                                <input
                                    type="text"
                                    value={guestName}
                                    onChange={(e) => setGuestName(e.target.value)}
                                    placeholder="Customer name"
                                />
                            </div>
                            <div className="ls-form-group">
                                <label>Mobile *</label>
                                <input
                                    type="text"
                                    value={guestMobile}
                                    onChange={(e) => setGuestMobile(e.target.value)}
                                    placeholder="10-digit mobile"
                                    maxLength={10}
                                />
                            </div>
                            <div className="ls-form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={guestEmail}
                                    onChange={(e) => setGuestEmail(e.target.value)}
                                    placeholder="Optional"
                                />
                            </div>
                        </div>
                    )}

                    <div className="ls-form-group">
                        <label>Search Products *</label>
                        <input
                            type="text"
                            placeholder="Type product name..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                        />
                        {filteredProducts.length > 0 && (
                            <div className="product-search-results">
                                {filteredProducts.map((product) => (
                                    <div
                                        key={`${product.product_id}-${product.sku || product.title}`}
                                        className="product-select-card"
                                        onClick={() => addProduct(product)}
                                    >
                                        {product.image && (
                                            <img src={`${url}${product.image}`} alt="" />
                                        )}
                                        <div className="product-info">
                                            <strong>{product.title}</strong>
                                            <p>
                                                ₹{product.discountPrice || product.salePrice || product.originalPrice || product.price}
                                                {product.sku ? <span className="sku"> (SKU: {product.sku})</span> : null}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {lineItems.length > 0 && (
                        <div className="selected-product-list" style={{ marginBottom: 12 }}>
                            {lineItems.map((item, index) => (
                                <div key={`${item.product_id}-${item.product_sku}-${index}`} className="selected-product-chip" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                    <span style={{ flex: 1 }}>{item.product_name}</span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={item.quantity}
                                        onChange={(e) => updateLineQty(index, e.target.value)}
                                        style={{ width: 56 }}
                                    />
                                    <span>₹{(Number(item.product_saleprice) * Number(item.quantity)).toFixed(2)}</span>
                                    <i className="fa fa-times" role="button" tabIndex={0} onClick={() => removeLine(index)} />
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="ls-form-row">
                        <div className="ls-form-group">
                            <label>Delivery Charge (₹)</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={deliveryCharge}
                                onChange={(e) => setDeliveryCharge(e.target.value)}
                            />
                        </div>
                        <div className="ls-form-group">
                            <label>Discount (₹)</label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={discountPrice}
                                onChange={(e) => setDiscountPrice(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ls-form-row">
                        <div className="ls-form-group">
                            <label>Payment Mode *</label>
                            <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                                {PAYMENT_MODES.map((mode) => (
                                    <option key={mode} value={mode}>{mode}</option>
                                ))}
                            </select>
                        </div>
                        <div className="ls-form-group">
                            <label>Order Status *</label>
                            <select value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)}>
                                {ORDER_STATUSES.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <h4 style={{ marginTop: 12 }}>Billing Address *</h4>
                    <div className="ls-form-row">
                        <div className="ls-form-group">
                            <label>Name</label>
                            <input
                                type="text"
                                value={billing.attention}
                                onChange={(e) => setBilling((p) => ({ ...p, attention: e.target.value }))}
                            />
                        </div>
                        <div className="ls-form-group">
                            <label>Mobile *</label>
                            <input
                                type="text"
                                value={billing.number}
                                onChange={(e) => setBilling((p) => ({ ...p, number: e.target.value }))}
                                maxLength={10}
                                disabled={guestMode}
                            />
                        </div>
                    </div>
                    <div className="ls-form-row">
                        <div className="ls-form-group">
                            <label>City *</label>
                            <input
                                type="text"
                                value={billing.city}
                                onChange={(e) => setBilling((p) => ({ ...p, city: e.target.value }))}
                            />
                        </div>
                        <div className="ls-form-group">
                            <label>Pincode *</label>
                            <input
                                type="text"
                                value={billing.pincode}
                                onChange={(e) => setBilling((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                                maxLength={6}
                            />
                        </div>
                    </div>
                    <div className="ls-form-group">
                        <label>Address *</label>
                        <input
                            type="text"
                            value={billing.new_address}
                            onChange={(e) => setBilling((p) => ({ ...p, new_address: e.target.value }))}
                        />
                    </div>

                    <div className="ls-form-group">
                        <label className="lsuv-bulk-delete-select" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                                type="checkbox"
                                checked={sameAsBilling}
                                onChange={(e) => setSameAsBilling(e.target.checked)}
                            />
                            <span>Shipping same as billing</span>
                        </label>
                    </div>

                    {!sameAsBilling && (
                        <>
                            <h4>Shipping Address *</h4>
                            <div className="ls-form-row">
                                <div className="ls-form-group">
                                    <label>City *</label>
                                    <input
                                        type="text"
                                        value={shipping.city}
                                        onChange={(e) => setShipping((p) => ({ ...p, city: e.target.value }))}
                                    />
                                </div>
                                <div className="ls-form-group">
                                    <label>Pincode *</label>
                                    <input
                                        type="text"
                                        value={shipping.pincode}
                                        onChange={(e) => setShipping((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
                                        maxLength={6}
                                    />
                                </div>
                            </div>
                            <div className="ls-form-group">
                                <label>Address *</label>
                                <input
                                    type="text"
                                    value={shipping.address}
                                    onChange={(e) => setShipping((p) => ({ ...p, address: e.target.value }))}
                                />
                            </div>
                        </>
                    )}

                    <div className="ls-form-group">
                        <label>Order Note</label>
                        <textarea
                            rows={2}
                            value={customerNote}
                            onChange={(e) => setCustomerNote(e.target.value)}
                            placeholder="Optional note for this order"
                        />
                    </div>

                    <div style={{ marginBottom: 12, fontWeight: 600 }}>
                        Total: ₹{amountAfterDiscount.toFixed(2)}
                        {Number(discountPrice) > 0 && (
                            <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 8 }}>
                                (before discount ₹{amountBeforeDiscount.toFixed(2)})
                            </span>
                        )}
                    </div>

                    <div className="ls-modal-action">
                        <button type="button" className="ls-modal-cancel" onClick={closeModal} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="ls-apply-btn" disabled={submitting}>
                            {submitting ? "Creating..." : "Create Order"}
                        </button>
                    </div>
                </form>
            </div>
            <ToastContainer />
        </div>
    );
};

export default AdminOrderFormModal;
